import {
  ApiAlarmsCountDataSourceSettings,
  ApiAlarmsCountQuerySettings,
  ApiBatteryHistogramDataSourceSettings,
  ApiBatteryHistogramQuerySettings,
  ApiDeviceSensorDataSourceSettings,
  ApiDeviceSensorQuerySettings,
  ApiDevicesBatteryDataSourceSettings,
  ApiDevicesBatteryQuerySettings,
  ApiDevicesSignalDataSourceSettings,
  ApiDevicesSignalQuerySettings,
  ApiDevicesTransmissionsDataSourceSettings,
  ApiDevicesTransmissionsQuerySettings,
  ApiSignalHistogramDataSourceSettings,
  ApiSignalHistogramQuerySettings,
  ApiReadingsDeviceGroupsDataSourceSettings,
  ApiReadingsDeviceGroupsQuerySettings,
  ApiReadingsDeviceSensorListDataSourceSettings,
  ApiReadingsDeviceSensorListQuerySettings,
  ApiReadingsDeviceSensorTypeListDataSourceSettings,
  ApiReadingsDeviceSensorTypeListQuerySettings,
  ApiReadingsMixedDataSourceSettings,
  ApiReadingsMixedQuerySettings,
  DataResult,
  DataSourceSettingsDto,
  DataSourceSettingsMutatePayload,
  DataStream,
  Device,
  DeviceReadingResponseDto,
  AlarmType,
  DeviceReadingsResponseDto,
  DeviceSensor,
  DeviceSensorType,
  Port,
  QuerySettingsDto,
  ReadingsRequestDto,
  ReadingsRequestSensorDto,
  Timespan,
  Transmission,
  TransmissionsDto,
  VariableDto,
} from '@thingslog/repositories';
import { TimespanFactory } from './Timespan.factory';
import { AxiosError, AxiosResponse } from 'axios';
import Axios from '../../../clients/Axios/Axios';
import { BatteryStatus } from '@thingslog/repositories/src/battery/BatteryStatus';
import { SignalStatus } from '@thingslog/repositories/src/signal/SignalStatus';
import { NumericDeviceReadingResponseDto } from '@thingslog/repositories/src/deviceCounter/DeviceReadingsResponseDto';
import { format } from 'date-fns';
import { SensorUtils } from '@thingslog/utils';

interface AlarmCountResponseItem {
  alarmType: AlarmType;
  count: number;
}

interface HistogramResponse {
  nbins: number;
  edges: number[];
  values: number[];
  total: number;
}

export class DataSourceSettingsFactory {
  private static compactMetaData(metaData: Record<string, string | number | null | undefined>): {
    [key: string]: string;
  } {
    return Object.entries(metaData).reduce(
      (
        result: { [key: string]: string },
        [key, value]: [string, string | number | null | undefined]
      ): { [key: string]: string } => {
        if (value === undefined || value === null || value === '') {
          return result;
        }

        result[key] = String(value);
        return result;
      },
      {}
    );
  }

  private static createReadingStreamMetaData({
    deviceName,
    deviceNumber,
    sensorName,
    sensorIndex,
    timeUnit,
    unit,
  }: {
    deviceName?: string;
    deviceNumber?: string;
    sensorName?: string;
    sensorIndex?: string | number;
    timeUnit?: string | null;
    unit?: string;
  }): { [key: string]: string } {
    return DataSourceSettingsFactory.compactMetaData({
      deviceName,
      deviceNumber,
      sensorName,
      sensorIndex,
      timeUnit,
      unit,
    });
  }

  public static toMutatePayload(source: DataSourceSettingsDto): DataSourceSettingsMutatePayload {
    const { id, ...rest } = source;
    return rest;
  }

  private static getNumericDeviceReadings(
    deviceReadings: DeviceReadingResponseDto[]
  ): NumericDeviceReadingResponseDto[] {
    return deviceReadings.filter(
      (deviceReadings: DeviceReadingResponseDto) => deviceReadings.sensorData === 'NUMERIC'
    ) as NumericDeviceReadingResponseDto[];
  }

  private static expandVariables(text: string, dashboardVariables: VariableDto[]): string[] {
    if (!dashboardVariables || dashboardVariables.length === 0) {
      return [text];
    }

    const variableMap = new Map<string, string | string[]>();
    dashboardVariables.forEach((variable: VariableDto) => {
      variableMap.set(variable.name, variable.value);
    });

    // Check if the text contains a variable placeholder
    const variableMatch = text.match(/\$\{([^}]+)\}|\{([^}]+)\}/);
    if (!variableMatch) {
      return [text];
    }

    const variableName = variableMatch[1] || variableMatch[2];
    const variableValue = variableMap.get(variableName);

    if (variableValue === undefined) {
      console.warn(`Variable ${variableName} not found in dashboard variables`);
      return [text];
    }

    if (Array.isArray(variableValue)) {
      return variableValue;
    }

    const stringValue = String(variableValue);
    return [stringValue];
  }

  public static execute(
    dataSource: DataSourceSettingsDto,
    query: QuerySettingsDto,
    defaultTimespan: Timespan,
    dashboardVariables: VariableDto[] = [],
    unformattedFromDate: string | null = null,
    unformattedToDate: string | null = null,
    companyId: number | null = null
  ): Promise<DataResult | null> {
    const fromDate = unformattedFromDate
      ? format(new Date(unformattedFromDate), 'yyyy-MM-dd')
      : null;
    const toDate = unformattedToDate ? format(new Date(unformattedToDate), 'yyyy-MM-dd') : null;

    if (dataSource.source.type !== query.type) {
      throw new Error('Data source type and query type do not match');
    }

    switch (dataSource.source.type) {
      case 'api-alarms-count':
        if (query.type !== 'api-alarms-count') {
          throw new Error('Data source type and query type do not match');
        }
        return this.apiAlarmsCountExecute(dataSource.source, query, defaultTimespan, companyId);
      case 'api-battery-histogram':
        if (query.type !== 'api-battery-histogram') {
          throw new Error('Data source type and query type do not match');
        }
        return this.apiBatteryHistogramExecute(dataSource.source, query, companyId);
      case 'api-device-sensor':
        if (query.type !== 'api-device-sensor') {
          throw new Error('Data source type and query type do not match');
        }
        return this.apiDeviceSensorExecute(
          dataSource.source,
          query,
          defaultTimespan,
          dashboardVariables,
          fromDate,
          toDate
        );
      case 'api-devices-battery':
        if (query.type !== 'api-devices-battery') {
          throw new Error('Data source type and query type do not match');
        }
        return this.apiDevicesBatteryExecute(
          dataSource.source,
          query,
          defaultTimespan,
          dashboardVariables,
          companyId
        );
      case 'api-devices-signal':
        if (query.type !== 'api-devices-signal') {
          throw new Error('Data source type and query type do not match');
        }
        return this.apiDevicesSignalExecute(
          dataSource.source,
          query,
          defaultTimespan,
          dashboardVariables,
          companyId
        );
      case 'api-devices-transmissions':
        if (query.type !== 'api-devices-transmissions') {
          throw new Error('Data source type and query type do not match');
        }
        return this.apiDevicesTransmissionsExecute(dataSource.source, query, companyId);
      case 'api-signal-histogram':
        if (query.type !== 'api-signal-histogram') {
          throw new Error('Data source type and query type do not match');
        }
        return this.apiSignalHistogramExecute(dataSource.source, query, companyId);
      case 'api-readings-mixed':
        if (query.type !== 'api-readings-mixed') {
          throw new Error('Data source type and query type do not match');
        }
        return this.apiReadingsMixedExecute(
          dataSource.source,
          query,
          defaultTimespan,
          dashboardVariables,
          fromDate,
          toDate
        );
      case 'api-readings-device-groups':
        if (query.type !== 'api-readings-device-groups') {
          throw new Error('Data source type and query type do not match');
        }
        return this.apiReadingsDeviceGroupsExecute(
          dataSource.source,
          query,
          defaultTimespan,
          dashboardVariables,
          fromDate,
          toDate
        );
      case 'api-readings-device-sensor-list':
        if (query.type !== 'api-readings-device-sensor-list') {
          throw new Error('Data source type and query type do not match');
        }
        return this.apiReadingsDeviceSensorListExecute(
          dataSource.source,
          query,
          defaultTimespan,
          dashboardVariables,
          fromDate,
          toDate
        );
      case 'api-readings-device-sensor-type-list':
        if (query.type !== 'api-readings-device-sensor-type-list') {
          throw new Error('Data source type and query type do not match');
        }
        return this.apiReadingsDeviceSensorTypeListExecute(
          dataSource.source,
          query,
          defaultTimespan,
          dashboardVariables,
          fromDate,
          toDate
        );
      default:
        throw new Error('Unsupported data source type');
    }
  }

  private static async apiAlarmsCountExecute(
    source: ApiAlarmsCountDataSourceSettings,
    query: ApiAlarmsCountQuerySettings,
    defaultTimespan: Timespan,
    companyId: number | null = null
  ): Promise<DataResult | null> {
    const timespan = query.timespan ?? defaultTimespan;
    const resolvedTimespan = TimespanFactory.resolve(timespan);

    const response: AxiosResponse<AlarmCountResponseItem[]> = await Axios.get(
      '/api/devices/alarms/count',
      {
        params: {
          fromDate: `${resolvedTimespan.fromDate}T00:00:00.000Z`,
          toDate: `${resolvedTimespan.toDate}T23:59:59.999Z`,
          companyId: companyId ?? null,
        },
      }
    );

    if (response.status !== 200) {
      return null;
    }

    return {
      streams: response.data.map((item: AlarmCountResponseItem) => ({
        type: 'series',
        data: [item.count],
        metaData: DataSourceSettingsFactory.compactMetaData({
          alarmType: item.alarmType,
        }),
      })),
      metaData: {},
    };
  }

  private static async apiBatteryHistogramExecute(
    source: ApiBatteryHistogramDataSourceSettings,
    query: ApiBatteryHistogramQuerySettings,
    companyId: number | null = null
  ): Promise<DataResult | null> {
    return this.apiHistogramExecute('/api/devices/battery/histogram', companyId);
  }

  private static async apiSignalHistogramExecute(
    source: ApiSignalHistogramDataSourceSettings,
    query: ApiSignalHistogramQuerySettings,
    companyId: number | null = null
  ): Promise<DataResult | null> {
    return this.apiHistogramExecute('/api/devices/signal/histogram', companyId);
  }

  private static async apiHistogramExecute(
    endpoint: string,
    companyId: number | null = null
  ): Promise<DataResult | null> {
    const response: AxiosResponse<HistogramResponse> = await Axios.get(endpoint, {
      params: {
        companyId: companyId ?? null,
      },
    });

    if (response.status !== 200 || !Array.isArray(response.data.edges)) {
      return null;
    }

    const values = Array.isArray(response.data.values) ? response.data.values : [];

    return {
      streams: response.data.edges.map((edge: number, index: number) => {
        const value = values[index];
        const numericValue = typeof value === 'number' && Number.isFinite(value) ? value : 0;

        return {
          type: 'series',
          data: [numericValue],
          metaData: DataSourceSettingsFactory.compactMetaData({
            binLabel: `${edge}%`,
            edge,
            total: response.data.total,
          }),
        };
      }),
      metaData: {
        nbins: String(response.data.nbins),
        total: String(response.data.total),
      },
    };
  }

  public static async apiDeviceSensorExecute(
    source: ApiDeviceSensorDataSourceSettings,
    query: ApiDeviceSensorQuerySettings,
    defaultTimespan: Timespan,
    dashboardVariables: VariableDto[] = [],
    fromDate: string | null = null,
    toDate: string | null = null
  ): Promise<DataResult | null> {
    const endpoint = `/api/v2/devices/{DEVICE_NUMBER}/{SENSOR_INDEX}/readings?fromDate={FROM_DATE}&toDate={TO_DATE}&every=500`;

    const deviceNumber = this.expandVariables(query.deviceNumber, dashboardVariables)[0];
    const sensorIndex = this.expandVariables(query.sensorIndex, dashboardVariables)[0];
    const timespan = query.timespan ?? defaultTimespan;
    const resolvedFromDate = fromDate ?? TimespanFactory.resolve(timespan).fromDate;
    const resolvedToDate = toDate ?? TimespanFactory.resolve(timespan).toDate;

    const endpointWithParams = endpoint
      .replace('{DEVICE_NUMBER}', deviceNumber)
      .replace('{SENSOR_INDEX}', sensorIndex)
      .replace('{FROM_DATE}', resolvedFromDate + 'T00:00:00Z')
      .replace('{TO_DATE}', resolvedToDate + 'T23:59:59Z')
      .replace('{EVERY}', '500');

    let deviceName = '';
    let sensorName = '';
    let unit = '';

    const response: AxiosResponse = await Axios.get(endpointWithParams);
    const deviceConfig: AxiosResponse<Device> = await Axios.get(`/api/v2/devices/${deviceNumber}`);
    if (deviceConfig.status === 200) {
      deviceName = deviceConfig.data.name ?? deviceNumber;
    }
    const ports: AxiosResponse<Record<number, Port>> = await Axios.get(
      `/api/devices/${deviceNumber}/initial-config/ports`
    );
    if (ports.status === 200) {
      const sensorIndexNum = parseInt(sensorIndex, 10);
      const foundPort: Port | undefined = ports.data[sensorIndexNum];
      if (foundPort) {
        const sensor = foundPort.sensor;
        sensorName = sensor.name ?? sensorIndex;
        unit = SensorUtils({ sensor }).getSensorUnit();
      }
    }

    const dataStream: DataStream = {
      type: 'timeseries',
      data: response.data.map((item: { date: string; reading: number }) => {
        return {
          date: item.date,
          value: item.reading,
        };
      }),
      metaData: DataSourceSettingsFactory.createReadingStreamMetaData({
        deviceName,
        deviceNumber,
        sensorName,
        sensorIndex,
        unit,
      }),
    };

    return new Promise(
      (
        resolve: (value: DataResult | PromiseLike<DataResult | null> | null) => void,
        reject: (reason?: unknown) => void
      ) => {
        try {
          if (response.status === 200) {
            const result: DataResult = {
              streams: [dataStream],
              metaData: {},
            };
            resolve(result);
          } else {
            resolve(null);
          }
        } catch (error) {
          reject(error);
        }
      }
    );
  }

  private static async apiDevicesBatteryExecute(
    source: ApiDevicesBatteryDataSourceSettings,
    query: ApiDevicesBatteryQuerySettings,
    defaultTimespan: Timespan,
    dashboardVariables: VariableDto[] = [],
    companyId: number | null = null
  ): Promise<DataResult | null> {
    const fromLevel = this.expandVariables(query.fromLevel.toString(), dashboardVariables)[0];
    const toLevel = this.expandVariables(query.toLevel.toString(), dashboardVariables)[0];

    // Expand multi-value variables for specific devices
    const specificDevices: string[] = [];
    query.specificDevices.forEach((device: string) => {
      const expandedDevices = this.expandVariables(device, dashboardVariables);
      specificDevices.push(...expandedDevices);
    });

    let response: AxiosResponse<BatteryStatus[]>;
    try {
      response = await Axios.get('/api/devices/battery/', {
        params: {
          fromLevel,
          toLevel,
          companyId: companyId ?? null,
        },
      });
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Failed to fetch battery data source', {
        status: axiosError.response?.status,
        data: axiosError.response?.data,
        message: axiosError.message,
      });
      throw error;
    }

    if (response.status !== 200) {
      return null;
    }

    return {
      streams: response.data
        .filter((item: BatteryStatus) => {
          if (specificDevices.length > 0) {
            return specificDevices.includes(item.deviceNumber);
          }
          return true;
        })
        .map((item: BatteryStatus) => {
          return {
            type: 'series',
            data: [item.level],
            metaData: DataSourceSettingsFactory.compactMetaData({
              deviceNumber: item.deviceNumber,
              deviceName: item.deviceName,
              voltage: item.voltage,
            }),
          };
        }),
      metaData: {},
    };
  }

  private static async apiDevicesSignalExecute(
    source: ApiDevicesSignalDataSourceSettings,
    query: ApiDevicesSignalQuerySettings,
    defaultTimespan: Timespan,
    dashboardVariables: VariableDto[] = [],
    companyId: number | null = null
  ): Promise<DataResult | null> {
    const fromLevel = this.expandVariables(query.fromLevel.toString(), dashboardVariables)[0];
    const toLevel = this.expandVariables(query.toLevel.toString(), dashboardVariables)[0];
    const selectedMetrics = query.metrics?.length ? query.metrics : ['level'];

    const specificDevices: string[] = [];
    query.specificDevices.forEach((device: string) => {
      const expandedDevices = this.expandVariables(device, dashboardVariables);
      specificDevices.push(...expandedDevices);
    });

    let response: AxiosResponse<SignalStatus[]>;
    try {
      response = await Axios.get('/api/devices/signal/', {
        params: {
          fromLevel,
          toLevel,
          companyId: companyId ?? null,
        },
      });
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Failed to fetch signal data source', {
        status: axiosError.response?.status,
        data: axiosError.response?.data,
        message: axiosError.message,
      });
      throw error;
    }

    if (response.status !== 200) {
      return null;
    }

    return {
      streams: response.data
        .filter((item: SignalStatus) => {
          if (specificDevices.length > 0) {
            return specificDevices.includes(item.deviceNumber);
          }
          return true;
        })
        .map((item: SignalStatus) => {
          return {
            type: 'series',
            data: [item.level, item.rssi],
            metaData: DataSourceSettingsFactory.compactMetaData({
              deviceNumber: item.deviceNumber,
              deviceName: item.deviceName,
              level: item.level,
              rssi: item.rssi,
              ber: item.ber,
              snr: item.snr,
              sf: item.sf,
            }),
          };
        }),
      metaData: {
        signalMetrics: selectedMetrics.join(','),
      },
    };
  }

  private static async apiDevicesTransmissionsExecute(
    source: ApiDevicesTransmissionsDataSourceSettings,
    query: ApiDevicesTransmissionsQuerySettings,
    companyId: number | null = null
  ): Promise<DataResult | null> {
    const selectedMetrics = query.metrics?.length ? query.metrics : ['days'];

    let response: AxiosResponse<TransmissionsDto>;
    try {
      response = await Axios.get('/api/transmissions', {
        params: {
          companyId: companyId ?? null,
        },
      });
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Failed to fetch transmissions data source', {
        status: axiosError.response?.status,
        data: axiosError.response?.data,
        message: axiosError.message,
      });
      throw error;
    }

    if (response.status !== 200 || !Array.isArray(response.data.transmissions)) {
      return null;
    }

    return {
      streams: response.data.transmissions.map((item: Transmission) => {
        return {
          type: 'series',
          data: [
            item.transmissionDaysNumber,
            item.lastTransmissionSeq,
            item.transmittedCountersNumber,
            item.isTransmitting ? 1 : 0,
          ],
          metaData: DataSourceSettingsFactory.compactMetaData({
            deviceNumber: item.deviceNumber,
            deviceName: item.deviceName,
            lastTransmissionDate: item.lastTransmissionDate,
            nextTransmissionDate: item.nextTransmissionDate,
            days: item.transmissionDaysNumber,
            transmissions: item.lastTransmissionSeq,
            count: item.transmittedCountersNumber,
            transmitting: item.isTransmitting ? 1 : 0,
          }),
        };
      }),
      metaData: {
        transmissionMetrics: selectedMetrics.join(','),
      },
    };
  }

  private static async apiReadingsMixedExecute(
    source: ApiReadingsMixedDataSourceSettings,
    query: ApiReadingsMixedQuerySettings,
    defaultTimespan: Timespan,
    dashboardVariables: VariableDto[] = [],
    fromDate: string | null = null,
    toDate: string | null = null
  ): Promise<DataResult | null> {
    const resolvedTimespan = query.timespan ?? defaultTimespan;
    const resolvedFromDate = fromDate ?? TimespanFactory.resolve(resolvedTimespan).fromDate;
    const resolvedToDate = toDate ?? TimespanFactory.resolve(resolvedTimespan).toDate;

    const endpoint = `/api/devices/fetch-readings?fromDate=${resolvedFromDate}T00:00:00Z&toDate=${resolvedToDate}T23:59:59Z`;

    const unwrappedDeviceGroupIds = query.deviceGroupIds
      .map((id: string) => {
        return this.expandVariables(id, dashboardVariables);
      })
      .flat()
      .filter((id: string, index: number, array: string[]) => array.indexOf(id) === index);
    const resolvedDeviceGroupIds = unwrappedDeviceGroupIds.map((id: string) => parseInt(id, 10));

    const unwrappedDeviceSensorList = query.deviceSensors.map((sensor: DeviceSensor) => {
      return {
        deviceNumber: this.expandVariables(sensor.deviceNumber, dashboardVariables)[0],
        sensorIndex: this.expandVariables(sensor.sensorIndex, dashboardVariables)[0],
      } as DeviceSensor;
    });
    const resolvedDeviceSensorList = unwrappedDeviceSensorList.map((sensor: DeviceSensor) => {
      return {
        deviceNumber: sensor.deviceNumber,
        sensorIndex: parseInt(sensor.sensorIndex, 10),
      } as ReadingsRequestSensorDto;
    });

    const resolvedDeviceSensorTypeList = query.deviceSensorTypes.map(
      (sensorType: DeviceSensorType) => {
        const deviceNumbers = sensorType.deviceNumbers
          .map((deviceNumber: string) => this.expandVariables(deviceNumber, dashboardVariables))
          .flat()
          .filter((id: string, index: number, array: string[]) => array.indexOf(id) === index);
        return {
          deviceNumbers: deviceNumbers,
          sensorType: this.expandVariables(sensorType.sensorType, dashboardVariables)[0],
        } as DeviceSensorType;
      }
    );

    const payload: ReadingsRequestDto = {
      preferFlow: query.preferFlow,
      deviceGroupIds: resolvedDeviceGroupIds,
      deviceSensorList: resolvedDeviceSensorList,
      deviceSensorTypeList: resolvedDeviceSensorTypeList,
    };

    const response: AxiosResponse<DeviceReadingsResponseDto> = await Axios.post(endpoint, payload);

    return new Promise(
      (
        resolve: (value: DataResult | PromiseLike<DataResult | null> | null) => void,
        reject: (reason?: unknown) => void
      ) => {
        try {
          if (response.status === 200) {
            const result: DataResult = {
              streams: this.getNumericDeviceReadings(response.data.devicesReadings).map(
                (deviceReadings: NumericDeviceReadingResponseDto) => {
                  return {
                    type: 'timeseries',
                    data: deviceReadings.readings,
                    metaData: DataSourceSettingsFactory.createReadingStreamMetaData({
                      deviceName: deviceReadings.deviceName,
                      deviceNumber: deviceReadings.deviceNumber,
                      sensorName: deviceReadings.sensorName,
                      sensorIndex: deviceReadings.sensorIndex,
                      timeUnit: deviceReadings.timeUnit,
                      unit: deviceReadings.sensorUnit,
                    }),
                  };
                }
              ),
              metaData: {},
            };
            resolve(result);
          } else {
            resolve(null);
          }
        } catch (error) {
          reject(error);
        }
      }
    );
  }

  private static async apiReadingsDeviceGroupsExecute(
    source: ApiReadingsDeviceGroupsDataSourceSettings,
    query: ApiReadingsDeviceGroupsQuerySettings,
    defaultTimespan: Timespan,
    dashboardVariables: VariableDto[] = [],
    fromDate: string | null = null,
    toDate: string | null = null
  ): Promise<DataResult | null> {
    const resolvedTimespan = query.timespan ?? defaultTimespan;
    const resolvedFromDate = fromDate ?? TimespanFactory.resolve(resolvedTimespan).fromDate;
    const resolvedToDate = toDate ?? TimespanFactory.resolve(resolvedTimespan).toDate;

    const endpoint = `/api/devices/fetch-readings?fromDate=${resolvedFromDate}T00:00:00Z&toDate=${resolvedToDate}T23:59:59Z`;

    const unwrappedDeviceGroupIds = query.deviceGroupIds
      .map((id: string) => {
        return this.expandVariables(id, dashboardVariables);
      })
      .flat()
      .filter((id: string, index: number, array: string[]) => array.indexOf(id) === index);
    const resolvedDeviceGroupIds = unwrappedDeviceGroupIds.map((id: string) => parseInt(id, 10));

    const payload: ReadingsRequestDto = {
      preferFlow: query.preferFlow,
      deviceGroupIds: resolvedDeviceGroupIds,
      deviceSensorList: [],
      deviceSensorTypeList: [],
    };

    const response: AxiosResponse<DeviceReadingsResponseDto> = await Axios.post(endpoint, payload);

    return new Promise(
      (
        resolve: (value: DataResult | PromiseLike<DataResult | null> | null) => void,
        reject: (reason?: unknown) => void
      ) => {
        try {
          if (response.status === 200) {
            const result: DataResult = {
              streams: this.getNumericDeviceReadings(response.data.devicesReadings).map(
                (deviceReadings: NumericDeviceReadingResponseDto) => {
                  return {
                    type: 'timeseries',
                    data: deviceReadings.readings,
                    metaData: DataSourceSettingsFactory.createReadingStreamMetaData({
                      deviceName: deviceReadings.deviceName,
                      deviceNumber: deviceReadings.deviceNumber,
                      sensorName: deviceReadings.sensorName,
                      sensorIndex: deviceReadings.sensorIndex,
                      timeUnit: deviceReadings.timeUnit,
                      unit: deviceReadings.sensorUnit,
                    }),
                  };
                }
              ),
              metaData: {},
            };
            resolve(result);
          } else {
            resolve(null);
          }
        } catch (error) {
          reject(error);
        }
      }
    );
  }

  private static async apiReadingsDeviceSensorListExecute(
    source: ApiReadingsDeviceSensorListDataSourceSettings,
    query: ApiReadingsDeviceSensorListQuerySettings,
    defaultTimespan: Timespan,
    dashboardVariables: VariableDto[] = [],
    fromDate: string | null = null,
    toDate: string | null = null
  ): Promise<DataResult | null> {
    const resolvedTimespan = query.timespan ?? defaultTimespan;
    const resolvedFromDate = fromDate ?? TimespanFactory.resolve(resolvedTimespan).fromDate;
    const resolvedToDate = toDate ?? TimespanFactory.resolve(resolvedTimespan).toDate;

    const endpoint = `/api/devices/fetch-readings?fromDate=${resolvedFromDate}T00:00:00Z&toDate=${resolvedToDate}T23:59:59Z`;

    const unwrappedDeviceSensorList = query.deviceSensors.map((sensor: DeviceSensor) => {
      return {
        deviceNumber: this.expandVariables(sensor.deviceNumber, dashboardVariables)[0],
        sensorIndex: this.expandVariables(sensor.sensorIndex, dashboardVariables)[0],
      } as DeviceSensor;
    });
    const resolvedDeviceSensorList = unwrappedDeviceSensorList.map((sensor: DeviceSensor) => {
      return {
        deviceNumber: sensor.deviceNumber,
        sensorIndex: parseInt(sensor.sensorIndex, 10),
      } as ReadingsRequestSensorDto;
    });

    const payload: ReadingsRequestDto = {
      preferFlow: query.preferFlow,
      deviceGroupIds: [],
      deviceSensorList: resolvedDeviceSensorList,
      deviceSensorTypeList: [],
    };

    const response: AxiosResponse<DeviceReadingsResponseDto> = await Axios.post(endpoint, payload);

    return new Promise(
      (
        resolve: (value: DataResult | PromiseLike<DataResult | null> | null) => void,
        reject: (reason?: unknown) => void
      ) => {
        try {
          if (response.status === 200) {
            const result: DataResult = {
              streams: this.getNumericDeviceReadings(response.data.devicesReadings).map(
                (deviceReadings: NumericDeviceReadingResponseDto) => {
                  return {
                    type: 'timeseries',
                    data: deviceReadings.readings,
                    metaData: DataSourceSettingsFactory.createReadingStreamMetaData({
                      deviceName: deviceReadings.deviceName,
                      deviceNumber: deviceReadings.deviceNumber,
                      sensorName: deviceReadings.sensorName,
                      sensorIndex: deviceReadings.sensorIndex,
                      timeUnit: deviceReadings.timeUnit,
                      unit: deviceReadings.sensorUnit,
                    }),
                  };
                }
              ),
              metaData: {},
            };
            resolve(result);
          } else {
            resolve(null);
          }
        } catch (error) {
          reject(error);
        }
      }
    );
  }

  private static async apiReadingsDeviceSensorTypeListExecute(
    source: ApiReadingsDeviceSensorTypeListDataSourceSettings,
    query: ApiReadingsDeviceSensorTypeListQuerySettings,
    defaultTimespan: Timespan,
    dashboardVariables: VariableDto[] = [],
    fromDate: string | null = null,
    toDate: string | null = null
  ): Promise<DataResult | null> {
    const resolvedTimespan = query.timespan ?? defaultTimespan;
    const resolvedFromDate = fromDate ?? TimespanFactory.resolve(resolvedTimespan).fromDate;
    const resolvedToDate = toDate ?? TimespanFactory.resolve(resolvedTimespan).toDate;

    const endpoint = `/api/devices/fetch-readings?fromDate=${resolvedFromDate}T00:00:00Z&toDate=${resolvedToDate}T23:59:59Z`;

    const resolvedDeviceSensorTypeList = query.deviceSensorTypes.map(
      (sensorType: DeviceSensorType) => {
        const deviceNumbers = sensorType.deviceNumbers
          .map((deviceNumber: string) => this.expandVariables(deviceNumber, dashboardVariables))
          .flat()
          .filter((id: string, index: number, array: string[]) => array.indexOf(id) === index);
        return {
          deviceNumbers: deviceNumbers,
          sensorType: this.expandVariables(sensorType.sensorType, dashboardVariables)[0],
        } as DeviceSensorType;
      }
    );

    const payload: ReadingsRequestDto = {
      preferFlow: query.preferFlow,
      deviceGroupIds: [],
      deviceSensorList: [],
      deviceSensorTypeList: resolvedDeviceSensorTypeList,
    };

    const response: AxiosResponse<DeviceReadingsResponseDto> = await Axios.post(endpoint, payload);

    return new Promise(
      (
        resolve: (value: DataResult | PromiseLike<DataResult | null> | null) => void,
        reject: (reason?: unknown) => void
      ) => {
        try {
          if (response.status === 200) {
            const result: DataResult = {
              streams: this.getNumericDeviceReadings(response.data.devicesReadings).map(
                (deviceReadings: NumericDeviceReadingResponseDto) => {
                  return {
                    type: 'timeseries',
                    data: deviceReadings.readings,
                    metaData: DataSourceSettingsFactory.createReadingStreamMetaData({
                      deviceName: deviceReadings.deviceName,
                      deviceNumber: deviceReadings.deviceNumber,
                      sensorName: deviceReadings.sensorName,
                      sensorIndex: deviceReadings.sensorIndex,
                      timeUnit: deviceReadings.timeUnit,
                      unit: deviceReadings.sensorUnit,
                    }),
                  };
                }
              ),
              metaData: {},
            };
            resolve(result);
          } else {
            resolve(null);
          }
        } catch (error) {
          reject(error);
        }
      }
    );
  }
}
